import { AfterViewInit, Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { CommonService } from '../../../services/common/common.service';
import { AlignDirection, CellTemplateType, FilterTemplateType, GenericColDef, GridRowSelectionType , CellActionType } from '../../../models/grid-context';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { RestService } from '../../../services/rest/rest.service';
import { Endpoint, HelpIndex, ReviewSystem } from '../../../models/enums';
import { GenericGridComponent } from '../../generic-grid/generic-grid.component';
import { ContextMenuOption } from '../../../models/context-menu-option';
import { ReefReviewJobStatus, ReefReviewJobStatusType } from '../../../models/reef-review-job-status';
import { HttpService } from '../../../services/http/http.service';
import { GenericTreeNode } from '../../generic-tree/generic-tree-node';
import { isNullOrUndefined } from '../../../models/utils';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { RowNode } from 'ag-grid-community';
import { NotificationModalComponent } from '../../modals/notification-modal/notification-modal.component';

@Component({
  standalone: false,
  selector: 'res-reef-review-promotion-tracking',
  templateUrl: './reef-review-promotion-tracking.component.html',
  styleUrls: ['./reef-review-promotion-tracking.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ReviewPromotionTrackingComponent extends BaseModalComponent implements AfterViewInit {
  @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;
  @ViewChild('reefReview', { read: ElementRef, static: true }) reefReviewLink!: ElementRef;
  @ViewChild('downloadLink', { read: ElementRef, static: true }) downloadLink: ElementRef;

  // modal info
  modalTitle = 'Review Promotion Tracking';
  modalClass = 'reef-review-promotion-tracking-modal';
  automationId = 'reef-review-promotion-tracking';
  secondaryButtonLabel = 'Close';
  gridName = 'Review Promotion Tracking';
  isBusy = false;
  isBusyMessage = 'Loading Review Promotion Tracking ...';
  modalHelpIndex = HelpIndex.sys_reefreview_tracker_dlg;
  jobStatusItems: Array<string> = ['All', ...this.commonService.getEnumNames(ReefReviewJobStatusType)];
  filters: [] = [];

  requestInfo = new DrRequestInfo({
    endpoint: Endpoint.projectManager_listReviewSystemImports,
    responseName: 'reviewSystemImports',
    data: {
      projectId: this.commonService.currentProject?.handle,
      filters: this.filters
    },
  });

  columnDefs: Array<GenericColDef> = new Array<GenericColDef>(
    {
      headerName: 'Job Status',
      field: 'uploadState',
      cellRenderer: 'gridWorkbasketActionsCellRenderer',
      automationId: this.automationId,
      serviceTierFilter: true,
      filterType: FilterTemplateType.singleChoice,
      filterAttributeName: 'STATUS',
      filterItems: this.jobStatusItems,
      filterItemSelected: 'All',
      width: 90,
      minWidth: 60,
    },
    {
      headerName: 'Job Description',
      field: 'jobDescription',
      automationId: this.automationId,
      minWidth: 60,
    },
    {
      headerName: 'Associated Project',
      field: 'associatedProject',
      automationId: this.automationId,
      suppressAutoSize: true,
      width: 90,
      minWidth: 60,
    },
    {
      headerName: 'Results Count',
      field: 'resultCount',
      automationId: this.automationId,
      cellAlignDirection: AlignDirection.right,
      width: 70,
      minWidth: 30,
    },
    {
      headerName: 'Error Count',
      field: 'errorCount',
      automationId: this.automationId,
      cellAlignDirection: AlignDirection.right,
      width: 70,
      minWidth: 30,
    },
    {
      headerName: 'User',
      field: 'email',
      serviceTierFilter: true,
      automationId: this.automationId,
      filterType: FilterTemplateType.freeform,
      filterAttributeName: 'EMAIL',
      suppressAutoSize: true,
      minWidth: 60,
    },
    {
      headerName: 'Date Started',
      field: 'startTime',
      automationId: this.automationId,
      cellAlignDirection: AlignDirection.right,
      width: 120,
      minWidth: 60,
    },
    {
      headerName: 'Date Completed',
      field: 'endTime',
      automationId: this.automationId,
      cellAlignDirection: AlignDirection.right,
      width: 120,
      minWidth: 60,
    },
    {
      headerName: 'Duration',
      field: 'duration',
      automationId: this.automationId,
      cellAlignDirection: AlignDirection.right,
      width: 75,
      minWidth: 30,
    },
    {
      headerName: 'Job ID',
      field: 'importJobId',
      automationId: this.automationId,
      suppressAutoSize: true,
      minWidth: 60,
    },
    {
      automationId: this.automationId,
      cellTemplateType: CellTemplateType.contextMenu,
      minWidth: 30, maxWidth: 30, width: 30,
      suppressSizeToFit: true
    }
  );

  rowSelection = GridRowSelectionType.single;
  contextMenuOptions = [
    new ContextMenuOption({
      name: 'Download Error Report ...',
      iconClass: 'fa-list-alt',
      iconColor: 'color-blue-grey-03',
      iconSecondClass: 'fa-download',
      iconSecondColor: 'color-blue-01',
      automationId: 'reef-review-promotion-tracking-download',
      enabled: (item) => (item.uploadState === 'Failed' || item.uploadState === 'Completed with Errors') && (item.importJobId !== undefined ),
      click: (item) => this.downloadFilteredGrid(item.importJobStateId)
    }),
     new ContextMenuOption({
      name: 'Retry',
      iconClass: 'fa-retweet',
      iconColor: 'color-blue-01',
      automationId: 'reef-review-promotion-tracking-retry',
      enabled: (item) => (item.uploadState === ReefReviewJobStatusType.FAILED || item.uploadState === 'Completed With Errors') && isNullOrUndefined(item.retryNotes),
      click: (item) => this.retry(item)
    }),
    new ContextMenuOption({
      divider: true
    }),
    new ContextMenuOption({
      name: 'Task Notification ...',
      iconClass: 'fa-bell-o',
      iconColor: 'color-grey-10',
      automationId: 'reef-review-promotion-tracking-task-notification',
      enabled: (item) => (item.uploadState === ReefReviewJobStatusType.RUNNING ||
        item.uploadState === ReefReviewJobStatusType.PAUSED ||
        item.uploadState === ReefReviewJobStatusType.QUEUED ||
        item.uploadState === ReefReviewJobStatusType.PAUSING ||
        item.uploadState === ReefReviewJobStatusType.RESUMING ||
        item.uploadState === ReefReviewJobStatusType.REGISTERED) && item.importJobId,
      click: (item) => this.taskNotification(item)
    })
  ];

  constructor(
    private modalService: BsModalService,
    private restService: RestService,
    private commonService: CommonService,
    private httpService: HttpService,
    private modalRef: BsModalRef,
) {
    super(modalService, modalRef);
}

  ngAfterViewInit(): void {
    this.genericGrid.refreshColumns();
  }

  responseCallback = (response: any) => {
    if (response?.reviewSystemImports?.length > 0) {
      response.reviewSystemImports = response.reviewSystemImports.map(imports => { return new ReefReviewJobStatus(imports); });

      this.genericGrid.setData(response.reviewSystemImports);
    }
    else {
      this.genericGrid.setData([]);
    }
  }

  downloadFilteredGrid(node: GenericTreeNode): void {
    this.restService.post(Endpoint.projectManager_getDownloadReviewSystemImportErrorUrl, {
      projectId: this.commonService.currentProject?.handle, jobStateId: node, reviewSystem: ReviewSystem.REEF_REVIEW
    }).then(response => {
      if (response) {
        if (response !== undefined && response.url !== null) {
          this.commonService.unzipFile(response.url, this.downloadLink.nativeElement, 'reviewImportErrorReport', this.filters);
        }
      }
    });
  }

  retry(item: ReefReviewJobStatus): void {
    this.isBusy = true;
    this.isBusyMessage = 'Retrying ...';
    this.restService.post(Endpoint.projectManager_retryUploadToReviewSystem, {
      projectId: this.commonService.currentProject?.handle,
      reviewSystemUploadId: item.reviewSystemUploadId,
      reviewProjectId: item.reviewProjectId,
      reviewSystem: ReviewSystem.REEF_REVIEW,
      notes: item.retryNotes
    }).then(response => {
      this.isBusy = false;
      if (response) {
        this.genericGrid.loadData();
      }
    }, error => {
      this.isBusy = false;
      this.genericGrid.loadData();
    });
  }

  taskNotification(item: ReefReviewJobStatus): void {
    this.commonService.openModal(NotificationModalComponent, {
      modalData: { item: [item], modalType: 'task-notification', currentEmail: this.commonService.currentUser.email }
    }).then(data => {
      const emails = data?.modalData?.emails.slice(0, -1).filter(email => email.trim() !== '');
      this.commonService.openInformationToastNotification(`Task notification will be Emailed to: ${this.displayEmails(emails)}.`);
    });
  }
  
  openHelp = (): void => this.commonService.openHelp(this.modalHelpIndex);
  
  cellClickedCallback = (rowNode: RowNode, action: CellActionType): void => {
    if (rowNode?.data) {
      if (action === CellActionType.workbasketIcon) {
        if (rowNode.data.uploadErrorMessage) {
          this.commonService.openErrorModal(rowNode.data.uploadErrorMessage, 'Task Failed', false);
        }
      }
    }
  }

  private displayEmails(emails: string[]): string {
    return emails.length > 3 ? `${emails.slice(0, 3).join('; ')} and others` : emails.join('; ');
  }

}
